/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { getSettings } from './settings';
import { LogLevel } from "@azure/msal-browser";

// this is legacy, once all apis moved to the 'useApi' way fo doing things, then delete between here....
let app = null;
export const setMsalInstance = (msalInstance) =>{
    app = msalInstance;
}
export const apiAccessToken = async () =>{
    if(app == null) throw new Error('Msal instance not set');
    const activeAccount = app.getActiveAccount();
      if(!activeAccount){
          await app.loginRedirect();
      }
      let apiReq = await genericRequest();
      const request = {
          ...apiReq,
          account: activeAccount
      };
      const authResult = await app.acquireTokenSilent(request);
      return authResult.accessToken;  
}
// ... and here (end delete)

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
const settings = await getSettings();
let appId = settings.appId;
let tenantId = settings.tenantId;

export const msalConfig = {
    auth: {
        clientId: appId, // This is the ONLY mandatory field that you need to supply.
        authority: "https://login.microsoftonline.com/".concat(tenantId), // Replace the placeholder with your tenant subdomain 
        redirectUri: '/',//window.location.origin, // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
// We should NOT be using /.default. We should use scopes like  esg-metric-metadata.read and esg-metric-metadata.write, but difficult to add with sacred
let defaultScopes = {
    read: [`${settings.apiUri}/.default`],
    write: [`${settings.apiUri}/.default`],
}

export const protectedResources = {
    fundApi :{
        endpoint() { return `${settings.apiBasePath}/funds` },
        scopes : defaultScopes 
    },
    fundIndicatorsApi :{
        endpoint(fundCode, indicatorId = null) { 
            let url = indicatorId? `${settings.apiBasePath}/funds/${fundCode}/fund-indicators/${indicatorId}`
            : `${settings.apiBasePath}/funds/${fundCode}/fund-indicators`
            return url;
        },
        scopes : defaultScopes 
    },
    indicatorsApi :{
        endpoint(indicatorId = null){ 
            let url = indicatorId? `${settings.apiBasePath}/indicators/${indicatorId}`
            : `${settings.apiBasePath}/indicators`
            return url;
        },
        scopes : defaultScopes 
    }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...protectedResources.fundApi.scopes.read, ...protectedResources.fundApi.scopes.write],
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
// export const silentRequest = {
//     scopes: ["openid", "profile"],
//     loginHint: "example@domain.net"
// };


// this is legacy, once all apis moved to the 'useApi' way fo doing things, then delete between here....
export const genericRequest = async() => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath,
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const indicatorsRequest = async() => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/indicators'),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const indicatorDetailsRequest = async(indicatorId) => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/indicators/').concat(indicatorId),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const indicatorsByNameTypeAndRegRequest = async(indicatorName, indicatorType, indicatorGroupName) => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/indicators?indicatorName=').concat(indicatorName).concat('&indicatorType=').concat(indicatorType).concat('&indicatorGroup=').concat(indicatorGroupName),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const metricToindicatorRequest = async(indicatorId) => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/indicators/').concat(indicatorId).concat('/metrics'),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const metricsRequest = async() => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/metrics'),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const metricDetailsRequest = async(metricId) => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/metrics/').concat(metricId),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const metricByNameRequest = async(metricName) => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/metrics?metricName=').concat(metricName),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const metricValueUnitTypesRequest = async() => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/metrics/*/unitvaluetypes'),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const metricSourceFieldsRequest = async() => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/metrics/*/sourcefields'),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};

export const indicatorGroupsRequest = async() => {
    const settings = await getSettings();
    let request =
     {
        url: settings.apiBasePath.concat('/indicator-groups'),
        scope: settings.apiUri.concat("/.default")
    }
    return request;
};
// ... and here (end delete)